import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import { QUERY_KEYS, SAFETY_MEETINGS_COLUMN } from '../../../constants';
import { usePagination } from '../../../hooks';
import { resetSafetyMeetingStore } from '../../../redux';
import { popup } from '../../../utils';
import { Table } from '../Table';
import { SearchIcon } from '../../../assets/svg';
import {
    createSafetyMeetingAssociationService,
    getAllSafetyMeetingService
} from '../../../utils/apiServices/safetyServiceAPI';
import { useSelector } from 'react-redux';
import SelectCustomerDD from '../../Admin/SafetyMeetings/SelectCustomerDD';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const AssignSafetyMeetingModal = ({ showModal, setShowModal }) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const [safetyMeetings, setSafetyMeetings] = useState({ data: [], count: 0 });
    const [search, setSearch] = useState('');
    const selectedSafetyMeetings = useSelector((s) => s.safetyMeetings.selectedSafetyMeetings);
    const safetyMeetingCustomer = useSelector((s) => s.safetyMeetings.safetyMeetingCustomer);
    const pagination = usePagination(safetyMeetings?.data.length, safetyMeetings?.count, 50);

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [QUERY_KEYS.SAFETY_MEETING, debouncedSearch, pagination.limit, pagination.offset],
        queryFn: async () =>
            await getAllSafetyMeetingService({
                search: debouncedSearch,
                offset: pagination.offset,
                limit: pagination.limit,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        enabled: showModal,
    });
    if (isError) popup('error', error.message);

    useEffect(() => {
        if (data && isFetched) {
            setSafetyMeetings(data);
        }
    }, [data, isFetched]);

    const assignMutation = useMutation({
        mutationFn: async (data) => await createSafetyMeetingAssociationService(data),
        onSuccess: ({ message }) => {
            popup('success', message + `: ${safetyMeetingCustomer.name}`);
            dispatch(resetSafetyMeetingStore());
            setShowModal(false);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SAFETY_MEETING_ASSOCIATION],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SAFETY_MEETING_ASSOCIATION],
                exact: false,
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SAFETY_MEETING_ASSOCIATION_BY_ID, safetyMeetingCustomer.id],
            });
        },
        onError: (error) => popup('error', error.message),
    });

    const assignedHandler = () => {
        if (selectedSafetyMeetings.length > 0 && safetyMeetingCustomer?.id) {
            const data = {
                meeting_ids: selectedSafetyMeetings.map((item) => item.id),
                account_id: safetyMeetingCustomer.id,
            };
            assignMutation.mutate(data);
        }
    };

    return (
        <Modal
            show={showModal}
            onHide={() => {
                dispatch(resetSafetyMeetingStore());
                setShowModal(false);
            }}
            size='xl'
            dialogClassName='overflow-hidden'
            fullscreen='lg-down'
        >
            <Modal.Header closeButton>
                <Modal.Title className='me-2'>Assign Safety Meetings</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '720px' }}>
                <div className='align-items-center d-flex gap-2 col-12 '>
                    <SelectCustomerDD />
                    <div
                        className=' rounded-2 custom-search-box d-flex align-items-center justify-content-start'
                        style={{ height: '46px', marginTop: '1.3rem' }}
                    >
                        <button className='bg-white p-0 border-0 d-flex align-items-center'>
                            <SearchIcon />
                        </button>
                        <input
                            type='text'
                            maxLength={40}
                            className='search-area border-0 w-100 py-1'
                            placeholder={'Search Safety Meetings...'}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className='modal-body p-0 pt-4'>
                    <Table
                        columns={SAFETY_MEETINGS_COLUMN}
                        data={safetyMeetings?.data || []}
                        pagination={pagination}
                        tableStyle={{ height: '500px', overflow: 'auto' }}
                        pageName='safetyMeeting(s)'
                        debouncedApiCall={()=>{}}
                        stickyColumns={[0, 'File Name']}
                        isLoading={isFetching}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex w-100 align-items-center justify-content-between modal-btn'>
                    <span
                        style={{ color: '#738da6', fontSize: '14px' }}
                    >{`${selectedSafetyMeetings.length} selected`}</span>
                    <Button
                        type='button'
                        onClick={assignedHandler}
                        className='table-footer-btn'
                        disabled={selectedSafetyMeetings.length === 0}
                    >
                        Assign
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignSafetyMeetingModal;
